import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { cardTransform, nextIndex, prevIndex, WHEEL_DEFAULTS } from './wheelGeometry.js';
import { Icon, type IconName } from '@/components/ui/icon.js';
import { ORIGINALS_ART } from '@/features/originals/catalog.js';
import { gameRtpBadge, PromoLockOverlay } from './GameCard.js';
import type { GameLike } from './GameCard.js';
import { useGamePromoLock } from '@/features/promo/use-game-lock.js';
import { cardTile, tileImage } from './tileImage.js';

interface HeroCarouselProps {
  games: GameLike[];
  onPlay?: (game: GameLike) => void;
}

/** Recognise the three in-house Originals so they get the mockup's exact look. */
function originalKind(g: GameLike): 'wheel' | 'scratch' | 'cards' | null {
  const c = (g.code ?? '').toLowerCase();
  const n = g.name.toLowerCase();
  if (c === 'wheel' || n.includes('wheel') || n.includes('fortune')) return 'wheel';
  if (c === 'scratch' || n.includes('scratch')) return 'scratch';
  if (c === 'cards' || c === 'card' || n.includes('card flip') || n.includes('flip')) return 'cards';
  return null;
}

// Decorative gradient + icon palette for the wheel showcase. The approved
// 12-hero-wheel-arc mockup uses one vivid colour + line-icon per card; the
// icon is eye-candy (the real game name/provider sits in the card label), so
// non-Originals get a deterministic slice of this palette to keep the arc
// colourful and varied instead of a wall of identical slot tiles.
const ART_PALETTE: { icon: IconName; gradient: string }[] = [
  { icon: 'trendUp', gradient: 'var(--art-1)' },
  { icon: 'cherry',  gradient: 'var(--art-2)' },
  { icon: 'spade',   gradient: 'var(--art-3)' },
  { icon: 'fish',    gradient: 'var(--art-4)' },
  { icon: 'gift',    gradient: 'var(--art-5)' },
  { icon: 'video',   gradient: 'var(--art-6)' },
  { icon: 'star',    gradient: 'var(--art-7)' },
  { icon: 'ticket',  gradient: 'var(--art-3)' },
];

function paletteIndex(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % ART_PALETTE.length;
}

function cardArt(g: GameLike): { icon: IconName; gradient: string } {
  // Known Originals (by code) use their canonical art so the wheel matches the
  // dropdown exactly; any other game falls back to the colourful palette.
  const code = (g.code ?? '').toLowerCase();
  const orig = ORIGINALS_ART[code];
  if (orig) return { icon: orig.icon, gradient: orig.grad };
  return ART_PALETTE[paletteIndex(g.id || g.name)];
}

// Empty string means "no badge" (callers gate on truthiness).
function rtpBadge(g: GameLike): string {
  return gameRtpBadge(g) ?? '';
}

export function HeroCarousel({ games, onPlay }: HeroCarouselProps) {
  const now = new Date();
  const { t } = useTranslation(['common', 'lobby']);
  // The same verdict the grid cards read. Without it the wheel was the one
  // surface that opened a game the rest of the lobby locks, and the player met
  // the refusal at svc-game instead of on the tile (#523).
  const { isLocked } = useGamePromoLock();
  const lockNote = t('lobby:promoLocked.card');
  const [cur, setCur] = useState(0);
  const hovering = useRef(false);
  const count = games.length;

  // Pointer drag → spin the wheel. We only enter drag mode once the pointer
  // travels past a threshold, so an ordinary click (with a few px of jitter)
  // never gets swallowed. One card per ~130px dragged; snap on release.
  const [dragDeg, setDragDeg] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ startX: 0, active: false, moved: false });
  const STEP = WHEEL_DEFAULTS.step;
  const PX_PER_STEP = 130;
  const DRAG_THRESHOLD = 10;

  // Drag handlers live on the wheel element. onPointerLeave commits the drag
  // so it never gets stuck if the button is released off the wheel.
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (count < 2) return;
    drag.current = { startX: e.clientX, active: true, moved: false };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    if (!drag.current.moved && Math.abs(dx) > DRAG_THRESHOLD) {
      drag.current.moved = true;
      setDragging(true);
      hovering.current = true;
    }
    if (!drag.current.moved) return;
    const lim = STEP * 2.5;
    setDragDeg(Math.max(-lim, Math.min(lim, (dx / PX_PER_STEP) * STEP)));
  };
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current.active) return;
    drag.current.active = false;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > DRAG_THRESHOLD) {
      const steps = Math.max(-3, Math.min(3, Math.round(dx / PX_PER_STEP)));
      if (steps !== 0) setCur((c) => ((c - steps) % count + count) % count);
    }
    setDragging(false);
    setDragDeg(0);
    window.setTimeout(() => { hovering.current = false; }, 150);
  };

  useEffect(() => {
    if (count < 2) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = window.setInterval(() => {
      if (!hovering.current) setCur((c) => nextIndex(c, count));
    }, 6000);
    return () => window.clearInterval(id);
  }, [count]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.key === 'ArrowRight') setCur((c) => nextIndex(c, count));
      if (e.key === 'ArrowLeft') setCur((c) => prevIndex(c, count));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count]);

  if (count === 0) return null;
  const featured = games[cur];
  const featLabel = originalKind(featured) ? 'Originals' : (featured.providerDisplayName ?? 'Originals');
  const featRtp = rtpBadge(featured);
  const featLocked = isLocked(featured);

  return (
    <div
      className="relative mx-auto mt-6 w-full max-w-[1080px]"
      onMouseEnter={() => { hovering.current = true; }}
      onMouseLeave={() => { hovering.current = false; }}
    >
      {/* ── Wheel arc ── */}
      <div
        className="relative h-[256px] select-none touch-pan-y [perspective:1600px] cursor-grab active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onPointerCancel={endDrag}
        onDragStart={(e) => e.preventDefault()}
      >
        <button
          aria-label={t('common.previous')}
          onClick={() => setCur((c) => prevIndex(c, count))}
          className="absolute left-2 top-[150px] z-[8] grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/10 text-white backdrop-blur transition hover:scale-105 hover:bg-[var(--accent)]/60"
        ><ChevronLeft className="size-5" /></button>
        <button
          aria-label={t('common.next')}
          onClick={() => setCur((c) => nextIndex(c, count))}
          className="absolute right-2 top-[150px] z-[8] grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/10 text-white backdrop-blur transition hover:scale-105 hover:bg-[var(--accent)]/60"
        ><ChevronRight className="size-5" /></button>

        <div className="absolute left-1/2 top-6">
          {games.map((g, i) => {
            const ct = cardTransform(i, cur - dragDeg / STEP, count, WHEEL_DEFAULTS);
            // The far-edge card wraps to the opposite side on each step; let it
            // teleport (no transition) instead of sweeping across the centre.
            const nearWrap = Math.abs(ct.offset) > count / 2 - 1;
            const noAnim = dragging || nearWrap;
            const art = cardArt(g);
            const tile = cardTile(g, now);
            // A Tile is the whole card: the game's art with its name and mark printed in.
            const tileSrc = tile ? tileImage(tile) : null;
            const cover = tileSrc ? null : g.thumbnailUrl;
            const rtp = rtpBadge(g);
            const locked = isLocked(g);
            return (
              <div
                key={g.id}
                draggable={false}
                aria-disabled={locked || undefined}
                onClick={() => {
                  if (drag.current.moved) return;
                  // A locked tile still centres, so the player can read why it
                  // is locked; it just never launches.
                  if (i !== cur) { setCur(i); return; }
                  if (!locked) onPlay?.(g);
                }}
                className={`absolute -left-[72px] w-36 ${locked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                style={{
                  transformOrigin: `50% ${WHEEL_DEFAULTS.radius}px`,
                  transform: `rotate(${ct.angle}deg)`,
                  transition: noAnim ? 'none' : 'transform 500ms cubic-bezier(0.2,0.8,0.2,1)',
                  zIndex: ct.z,
                  pointerEvents: ct.hidden ? 'none' : 'auto',
                }}
              >
                <div
                  style={{
                    transform: `scale(${ct.scale})`,
                    opacity: ct.opacity,
                    transition: noAnim ? 'none' : 'transform 500ms cubic-bezier(0.2,0.8,0.2,1), opacity 400ms ease',
                  }}
                >
                  <div
                    className={`relative flex aspect-[3/4] items-start overflow-hidden rounded-2xl p-2.5${
                      tileSrc ? ' transition-transform duration-200 ease-out hover:scale-[1.04]' : ''
                    }`}
                    style={{
                      background: cover || tileSrc ? undefined : art.gradient,
                      boxShadow: ct.active
                        ? '0 30px 64px var(--primary-glow), 0 0 0 2px var(--accent)'
                        : '0 22px 46px rgba(0,0,0,.55)',
                    }}
                  >
                    {/* Uploaded card art (BO → Games) replaces the built-in icon;
                        painted on the rounded layer, like GameCard, so no seam
                        shows at the corners. */}
                    {cover && (
                      <div
                        role="img"
                        aria-label={g.name}
                        className="absolute inset-0 rounded-[inherit] bg-cover bg-center"
                        style={{ backgroundImage: `url("${cover}")` }}
                      />
                    )}
                    {tileSrc && (
                      <img
                        data-game-tile
                        src={tileSrc.src}
                        srcSet={tileSrc.srcSet}
                        alt={g.name}
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                        className="absolute inset-0 h-full w-full rounded-[inherit] object-cover"
                      />
                    )}
                    {rtp && (
                      <span className="relative z-[2] rounded-md bg-[rgba(110,231,183,.95)] px-1.5 py-[3px] text-[9px] font-extrabold text-[#04150f]">
                        {rtp}
                      </span>
                    )}
                    {!cover && !tileSrc && (
                      <div className="absolute inset-0 grid place-items-center">
                        <span style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,.5))' }}>
                          <Icon name={art.icon} size={54} className="text-white" />
                        </span>
                      </div>
                    )}
                    {!tileSrc && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[rgba(5,7,14,.96)] to-transparent px-2.5 pb-2.5 pt-[18px]">
                        <div className="text-[12.5px] font-extrabold leading-[1.05] text-white line-clamp-1">{g.name}</div>
                        <div className="mt-0.5 text-[9px] font-bold text-[var(--accent-strong)] truncate">
                          {g.providerDisplayName ?? 'Originals'}
                        </div>
                      </div>
                    )}
                    {locked && <PromoLockOverlay note={lockNote} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Featured game + play ── */}
      <div className="relative z-[6] -mt-1 text-center">
        <div className="text-[19px] font-extrabold leading-tight text-white">{featured.name}</div>
        <div className="mt-0.5 text-[11px] text-[var(--text-secondary)]">
          {featLabel}{featRtp ? ` · ${featRtp}` : ''}
        </div>
        <button
          onClick={() => { if (!featLocked) onPlay?.(featured); }}
          disabled={featLocked}
          title={featLocked ? lockNote : undefined}
          className={`mx-auto mt-3 flex h-11 items-center gap-2 rounded-xl px-6 text-sm font-extrabold shadow-[0_8px_24px_var(--accent-glow)] transition ${
            featLocked
              ? 'cursor-not-allowed bg-[var(--bg-elevated)] text-[var(--text-muted)] shadow-none'
              : 'bg-[var(--accent)] text-[var(--accent-on)] hover:bg-[var(--accent-strong)]'
          }`}
        >
          {featLocked ? (
            <><Icon name="lock" size={16} /> {t('lobby:promoLocked.button')}</>
          ) : (
            <><Play className="size-4 fill-current" /> {t('common.play')} {featured.name}</>
          )}
        </button>
        <div className="mt-3 flex justify-center gap-1.5">
          {games.map((g, i) => (
            <button
              key={g.id}
              aria-label={g.name}
              onClick={() => setCur(i)}
              className={`h-[7px] rounded-full transition-all ${i === cur ? 'w-5 bg-[var(--accent-strong)]' : 'w-[7px] bg-white/20'}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
